import { ContentLoading } from "../components/ContentLoading";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useSyncExternalStore } from "react";
import { documentMarkdown } from "../knowledge/document-markdown";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { FieldError } from "../components/ui/field";
import { DocumentBody } from "../knowledge/DocumentBody";
import type { DocumentSession } from "../knowledge/document-session";
import { getOpsDocsPage } from "./docs-api";

function Comparison({
  pageId,
  session,
  onClose,
}: {
  pageId: string;
  session: DocumentSession;
  onClose: () => void;
}) {
  const latest = useQuery({
    queryKey: ["ops", "docs", "compare", pageId],
    queryFn: () => getOpsDocsPage(pageId),
    staleTime: 0,
  });
  const client = useQueryClient();
  const { draft } = useSyncExternalStore(session.subscribe, session.snapshot);
  const useSaved = () => {
    if (!latest.data) return;
    const page = latest.data.page;
    session.useSavedVersion(
      {
        title: page.title,
        markdown: documentMarkdown(page.blocks),
        links: page.links.map(({ entityType, entityId }) => ({ entityType, entityId })),
      },
      page.revision,
    );
    client.setQueryData(["ops", "docs", "page", pageId], latest.data);
    onClose();
  };
  return (
    <>
      <DialogBody>
        {latest.error ? <FieldError>{latest.error.message}</FieldError> : null}
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <section className="min-w-0">
            <h3 className="mb-2 text-xs font-semibold text-muted-foreground">Your unsaved draft</h3>
            <h2 className="mb-4 text-xl font-semibold">{draft.title}</h2>
            <DocumentBody markdown={draft.markdown} pageId={pageId} />
          </section>
          <section className="min-w-0 border-t pt-4 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-6">
            <h3 className="mb-2 text-xs font-semibold text-muted-foreground">
              Latest saved version
            </h3>
            {latest.isPending ? <ContentLoading label="Loading…" variant="document" /> : null}
            {latest.data ? (
              <>
                <h2 className="mb-4 text-xl font-semibold">{latest.data.page.title}</h2>
                <DocumentBody
                  markdown={documentMarkdown(latest.data.page.blocks)}
                  pageId={pageId}
                />
              </>
            ) : null}
          </section>
        </div>
      </DialogBody>
      <DialogFooter>
        <Button variant="ghost" onClick={onClose}>
          Keep my draft open
        </Button>
        <Button disabled={!latest.data || latest.isFetching} onClick={useSaved}>
          Use saved version
        </Button>
      </DialogFooter>
    </>
  );
}

export function DocsConflictReview({
  pageId,
  session,
}: {
  pageId: string;
  session: DocumentSession;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger render={<Button variant="outline" className="mb-4" />}>
        Compare versions
      </DialogTrigger>
      <DialogContent className="sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Review conflicting changes</DialogTitle>
          <DialogDescription>
            You can copy text between versions or download your draft. Choosing “Use saved version”
            replaces your unsaved draft with the latest saved document.
          </DialogDescription>
        </DialogHeader>
        {open ? (
          <Comparison
            pageId={pageId}
            session={session}
            onClose={() => {
              setOpen(false);
            }}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
